import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { ColWithGap, RowWithGap, RowWithSpaceBetween } from '@components/styles';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import ReservationCheckResult from '@components/ringil3/Features/Reservation/Helper/ReservationCheckResult';
import ErrorCountBadge from '@components/ringil3/Badges/ErrorBadge';
import useForm from '@hooks/useForm/useForm';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import {
  ActionTypesEnum,
  ComputeReservationDurationConfiguration,
  ExternalOrdersApiTypeEnum,
  FreeSlotsWrapperOutDTO,
  GetExternalOrdersResponse,
  MessageResponse,
  ReservationActionTypesEnum,
  ReservationFlagDTO,
} from '@api/ringil-api.v2';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue';
import { ClickableCursor, ColOdd, HorizontalLine, TwoColGrid } from '@components/ringil3/styles';
import { isNotNullOrUndefined, isNullOrUndefined, parseNumberOrUndefined } from '@utils/utils';
import InputManipulationType from '@components/ringil3/Input/InputManipulationType/InputManipulationType';
import { isExternalIdPresent, Order, orderSchema } from '@components/framework/orders/common';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { generateTimeslotOrderConfig } from '@components/ringil3/Features/Reservation/orderConfig.ts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import Tabs from '@components/ringil3/RingilTabs/Tabs.tsx';
import dayjs from 'dayjs';
import { useDebounce } from 'use-debounce';
import {
  apiPragueFromDayjsInTz,
  apiUtcFromDayjs,
  dayjsFromApiUtcInTz,
  dayjsFromDayAndTimeStrings,
  stringHHmmFromStringInTz,
} from '@utils/dateWithTz.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import SvgAvailableDates from '@components/ringil3/icons/reservation/AvailableDates.tsx';
import Panel from '@components/ringil3/Panel/Panel.tsx';
import SvgClose from '@components/ringil3/icons/panel/Close.tsx';
import COSelect from '@components/ringil3/COSelect/COSelect.tsx';
import InputCompanyRamp from '@components/ringil3/Input/InputCompanyRamp/InputCompanyRamp.tsx';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix.tsx';
import InputHourManual from '@components/ringil3/Input/InputHour/InputHourManual.tsx';
import SvgRefresh from '@components/ringil3/icons/Refresh.tsx';
import { ProgressSpinner } from 'primereact/progressspinner';
import RingilCalendar from '@components/framework/input/RingilCalendar.tsx';
import { useExternalOrdersDateRange } from '@components/ringil3/Features/Reservation/Helper/useExternalOrdersDateRange';
import { shouldDoubleDuration } from '@components/ringil3/Features/Reservation/Helper/shouldDoubleDuration';
import NoAvailableSlotsMessage from '@components/RingilComponents/NoAvailableSlots/NoAvailableSlotsMessage.tsx';
import { toEditableFlags } from '@components/RingilComponents/form/flagUnitsLimit';

interface ComponentProps {
  reservationId: string;
  applicationId: string;
  onRefresh: () => void;
  companyLocationCustomerId: string;
}

interface Form {
  actionTypes: ReservationActionTypesEnum[];
  orders: Order[];
  companyLocationId: string;
  rampId: string;
  localDate: string;
  preferredDate?: string;
  durationInMin: number;
  arrivalHourString: string;
  timezoneId: string;
}

export const useCreateEditReservationOrdersSidebar = () => {
  const [visible, setVisible] = useState(false);

  const EditReservationOrdersSidebar: React.FC<ComponentProps> = ({ reservationId, applicationId, onRefresh, companyLocationCustomerId }) => {
    const { tr } = useTranslationLgs();
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const {
      getReservationDetail,
      updateReservationOrdersAndTimeslot,
      getCreateEditReservationSettings,
      computeReservationDurationWithManipulationUnits,
      checkReservation,
      getFreeSlotsForLocationReservation,
      getExternalOrders,
    } = useContext(ApiContext);
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const [settings, setSettings] = useState<ComputeReservationDurationConfiguration>();
    const [activeTab, setActiveTab] = useState(0);
    const [showPlan, setShowPlan] = useState(false);
    const [reservationValid, setReservationValid] = useState(undefined);
    const [reservationCheckMessages, setReservationCheckMessages] = useState<MessageResponse[]>([]);
    const [slots, setSlots] = useState<FreeSlotsWrapperOutDTO[]>();
    const [loading, setLoading] = useState(false);
    const [checkReservationRequest, setCheckReservationRequest] = useState(false);
    const [carrierId, setCarrierId] = useState<string | undefined>(undefined);
    const [flags, setFlags] = useState<ReservationFlagDTO[]>([]);
    // RD-2869: the expedition Location the reservation is linked to - free slots/check are restricted to its opening hours
    const [linkedExpeditionLocationId, setLinkedExpeditionLocationId] = useState<string | null>(null);
    const [externalOrdersResponse, setExternalOrdersResponse] = useState<GetExternalOrdersResponse | undefined>(undefined);

    useLayoutEffect(() => {
      getCreateEditReservationSettings(companyLocationCustomerId, d => {
        setSettings(d);
      });
    }, []);

    useEffect(() => {
      const ac = new AbortController();

      getReservationDetail(
        reservationId,
        d => {
          if (ac.signal.aborted) return;

          setCarrierId(d.carrierId);
          // Sent back with the slot checks, which reject `discrepancyUnits` like the create does.
          setFlags(toEditableFlags(d.flags) ?? []);
          setLinkedExpeditionLocationId(d.linkedToExpeditionLocationId ?? null);
          form.overwriteValues({
            actionTypes: d.reservationActionTypes,
            orders:
              d.orders.length > 0
                ? d.orders.map(
                    t =>
                      ({
                        id: t.id,
                        orderName: t.orderName,
                        orderValue: t.orderValue,
                        orderAmount: t.orderAmount,
                        orderDescription: t.orderDescription,
                        orderNote: t.orderNote,
                        eanCodes: t.loadedEans,
                        externalOrders: t.externalData?.map(eo => ({
                          orderNumber: eo.orderNumber,
                          timeFrom: eo.timeFrom,
                          timeTo: eo.timeTo,
                        })),
                        items: t.items.map(i => ({
                          amount: i.amount,
                          cargoTemplateCode: i.cargoTemplate?.code,
                          stackable: i.stackable,
                          itemNote: i.itemNote,
                          decimalPlaces: i.cargoTemplate?.decimalPlaces,
                          height: i.height,
                          width: i.width,
                          length: i.length,
                          weight: i.weight,
                        })),
                      } as Order),
                  )
                : [
                    {
                      items: [],
                      orderAmount: null,
                      orderDescription: null,
                      orderName: null,
                      orderNote: null,
                      orderValue: null,
                    },
                  ],
            localDate: d.slot?.localDate ?? d.preferredDate ?? dayjs().format('YYYY-MM-DD'),
            rampId: d.rampId,
            arrivalHourString: d.slot?.utcStart ? stringHHmmFromStringInTz(d.slot.utcStart, d.slot?.timezoneId ?? 'Europe/Prague') : undefined,
            durationInMin: d.slot?.durationInMin ? Math.floor(d.slot?.durationInMin) : 30,
            companyLocationId: d.locationId,
            preferredDate: d.preferredDate,
            timezoneId: d.slot?.timezoneId ?? 'Europe/Prague',
          });
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
      return () => {
        ac.abort();
      };
    }, [reservationId]);

    const isOwner = loggedUser.companyId === companyLocationCustomerId;
    const reservationActionTypeEnabled = isOwner ? settings?.ownerShowActionTypes : settings?.counterPartyShowActionTypes;
    const reservationActionTypeRequired =
      reservationActionTypeEnabled === true && (isOwner ? settings?.ownerActionTypeRequired == true : settings?.counterPartyActionTypeRequired == true);
    const atLeastOneActionType = tr('generic.atLeastOneHandlingType', 'Select at least one handling type');

    const form = useForm<Form>(
      yup.object().shape({
        orders: orderSchema(isExternalIdPresent(externalOrdersResponse)),
        actionTypes: reservationActionTypeRequired
          ? yup.array().nullable().required(atLeastOneActionType).min(1, atLeastOneActionType)
          : yup.array().nullable(),
      }),
      null,
      d => save(d),
      false,
      false,
      null,
      'scroll',
    );

    useEffect(() => {
      if (
        settings?.externalOrdersApiType === ExternalOrdersApiTypeEnum.NOTINO_PAP &&
        isNotNullOrUndefined(companyLocationCustomerId) &&
        isNotNullOrUndefined(form.values?.companyLocationId) &&
        isNotNullOrUndefined(carrierId)
      ) {
        getExternalOrders(companyLocationCustomerId, carrierId, form.values.companyLocationId, d => {
          setExternalOrdersResponse(d);
        });
      } else {
        setExternalOrdersResponse(undefined);
      }
    }, [settings?.externalOrdersApiType, companyLocationCustomerId, form.values?.companyLocationId, carrierId]);

    const mapOrder = (order: Order) => {
      return {
        id: order.id,
        orderName: order.orderName,
        orderValue: order.orderValue,
        cargoAmount: order.orderAmount,
        cargoDescription: order.orderDescription,
        orderNote: order.orderNote,
        externalOrders: isExternalIdPresent(externalOrdersResponse) ? order.externalOrders : undefined,
        items: order.items.map(i => ({
          amount: i.amount,
          cargoTemplateCode: i.cargoTemplateCode,
          stackable: i.stackable,
          itemNote: i.itemNote,
          decimalPlaces: i.decimalPlaces,
          height: i.height,
          width: i.width,
          length: i.length,
          weight: i.weight,
        })),
      };
    };

    const save = (data: Form) => {
      setLoading(true);
      updateReservationOrdersAndTimeslot(
        reservationId,
        {
          orders: data.orders.map(mapOrder),
          actionTypes: data.actionTypes.map(t => ActionTypesEnum[t]),
          timeslot: {
            rampId: data.rampId,
            localDate: data.localDate,
            sinceHourString: data.arrivalHourString,
            durationInMinutes: data.durationInMin,
          },
        },
        () => {
          setLoading(false);
          setVisible(false);
          onRefresh();
        },
        null,
        {
          onValidationFailed: v => {
            setLoading(false);
            setBeValidationErrors(v);
          },
        },
      );
    };

    const requiredOderItems = isOwner ? settings?.ownerManipulationItemsRequired : settings?.counterPartyManipulationItemsRequired;
    const canEditDuration = isOwner ? settings?.editDuration == true : settings?.editDurationCounterParty == true;
    const optionalDimensions = form?.values?.orders?.some(order =>
      order.items?.some(item =>
        item.height != null || item.width != null || item.length != null || item.weight != null
      )
    );

    const [changedOrders] = useDebounce(form?.values?.orders, 700);

    useEffect(() => {
      if (isNullOrUndefined(changedOrders)) return;
      setCheckReservationRequest(true);
      computeReservationDurationWithManipulationUnits(
        companyLocationCustomerId,
        {
          items: form.values.orders
            .flatMap(o => o.items)
            .filter(t => t.cargoTemplateCode !== null)
            .map(i => ({ cargoItemType: i.cargoTemplateCode, quantity: i.amount })),
        },
        d => {
          const multiplier = shouldDoubleDuration(settings, form.values.actionTypes) ? 2 : 1;
          const roundedDuration = Math.floor(d.computedDuration * multiplier);
          if (roundedDuration !== form.values.durationInMin) {
            form.setFieldValue(form.names.durationInMin, roundedDuration);
          } else {
            setCheckReservationRequest(false);
          }
        },
      );
    }, [changedOrders, form.values?.actionTypes, settings?.doubleLoadingUnloadingTime]);

    const [stateForDeferred, setStateForDeferred] = useState(undefined);
    const [debouncedTimeslotState] = useDebounce(stateForDeferred, 700);

    // Update deferred state when any timeslot field changes
    useEffect(() => {
      if (form.values?.rampId && form.values?.durationInMin && form.values?.arrivalHourString && form.values?.localDate) {
        setStateForDeferred({
          rampId: form.values.rampId,
          durationInMin: form.values.durationInMin,
          arrivalHourString: form.values.arrivalHourString,
          localDate: form.values.localDate,
          timezoneId: form.values.timezoneId ?? 'Europe/Prague',
        });
      }
    }, [form.values?.arrivalHourString, form.values?.durationInMin, form.values?.rampId, form.values?.localDate]);

    const isValidItems = (orders: Order[]) => (
      Array.isArray(orders) &&
      orders.every(o =>
          !o.items
          || o.items.length === 0
          || (Array.isArray(o.items) &&
            o.items.length > 0 &&
            o.items.every(i =>
              isNotNullOrUndefined(i.amount) &&
              isNotNullOrUndefined(i.cargoTemplateCode) &&
              isNotNullOrUndefined(i.stackable),
            )
          ),
      )
    );

    const isTimeslotInPast = (timeslotState: any) => {
      if (!timeslotState) return false;
      const since = dayjsFromDayAndTimeStrings(timeslotState.localDate, timeslotState.arrivalHourString, timeslotState.timezoneId ?? 'Europe/Prague');
      return since.isBefore(dayjs());
    };

    const checkReservationHandler = () => {
      if (isNotNullOrUndefined(debouncedTimeslotState) && !isTimeslotInPast(debouncedTimeslotState) && isValidItems(form?.values?.orders)) {
        setCheckReservationRequest(true);
        const tz = debouncedTimeslotState.timezoneId ?? 'Europe/Prague';
        const sinceDayjs = dayjsFromDayAndTimeStrings(debouncedTimeslotState.localDate, debouncedTimeslotState.arrivalHourString, tz);
        const tillDayjs = sinceDayjs.add(debouncedTimeslotState.durationInMin, 'minute');
        checkReservation(
          [reservationId],
          {
            customerId: companyLocationCustomerId,
            timeslot: {
              since: apiPragueFromDayjsInTz(dayjsFromApiUtcInTz(sinceDayjs.toISOString(), tz), tz),
              till: apiPragueFromDayjsInTz(dayjsFromApiUtcInTz(tillDayjs.toISOString(), tz), tz),
              utcStart: apiUtcFromDayjs(sinceDayjs),
              utcEnd: apiUtcFromDayjs(tillDayjs),
              timezoneId: tz,
            },
            orders: form.values.orders.map(mapOrder),
            rampId: debouncedTimeslotState.rampId,
            locationId: form.values.companyLocationId,
            labelIds: [],
            linkToExpeditionLocationId: linkedExpeditionLocationId,
            openGate: false,
            createExpedition: false,
            flags,
            sendNotification: true,
          },
          () => {
            setCheckReservationRequest(false);
            setReservationValid(true);
            setReservationCheckMessages([]);
          },
          null,
          {
            onError: e => {
              setCheckReservationRequest(false);
              setReservationValid(false);
              // Unknown/transport error — no messages, the generic wording is shown
              setReservationCheckMessages([]);
              setShowPlan(true);
            },
            onValidationFailed: messages => {
              setCheckReservationRequest(false);
              setReservationValid(false);
              setReservationCheckMessages(messages);
              setShowPlan(true);
            },
          },
        );
      } else {
        setCheckReservationRequest(false);
      }
    };

    useEffect(() => {
      checkReservationHandler();
    }, [debouncedTimeslotState]);

    const { externalOrdersMinDate, externalOrdersMaxDate, externalOrdersMinIso } = useExternalOrdersDateRange(form.values?.orders);

    useEffect(() => {
      if (!externalOrdersMinDate) return;
      const current = form.values?.localDate;
      if (!current) return;
      const tz = form.values?.timezoneId ?? 'Europe/Prague';
      const minStr = dayjs(externalOrdersMinDate).format('YYYY-MM-DD');
      const maxStr = externalOrdersMaxDate ? dayjs(externalOrdersMaxDate).format('YYYY-MM-DD') : null;
      if (current < minStr || (maxStr && current > maxStr)) {
        form.setFieldValue('localDate', minStr);
        if (externalOrdersMinIso) {
          form.setFieldValue('arrivalHourString', stringHHmmFromStringInTz(externalOrdersMinIso, tz));
        }
        setShowPlan(true);
      }
    }, [externalOrdersMinDate, externalOrdersMaxDate, externalOrdersMinIso]);

    const offerAvailableSlots = () => {
      if (form.values.localDate && form.values.durationInMin && form.values.companyLocationId) {
        getFreeSlotsForLocationReservation(
          form.values.localDate,
          form.values.durationInMin,
          form.values.companyLocationId,
          [],
          {
            customerId: companyLocationCustomerId,
            rampId: null,
            locationId: form.values.companyLocationId,
            labelIds: [],
            orders: form.values.orders.map(mapOrder),
            linkToExpeditionLocationId: linkedExpeditionLocationId,
            openGate: false,
            createExpedition: false,
            flags,
            sendNotification: true,
          },
          d => {
            setSlots(d);
          },
        );
      }
    };

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          heading={
            <RowWithSpaceBetween>
              <div>{tr('EditReservationOrdersSidebar.title', 'Edit reservation orders')}</div>
              <ErrorCountBadge errorCount={new Set(form.validationErrors.map(t => t.path)).size} />
            </RowWithSpaceBetween>
          }
          onContinue={() => {
            form.validateAndSend();
          }}
          loading={checkReservationRequest || loading}
        >
          {isNotNullOrUndefined(form.values) && (
            <>
              <BackendValidationComponent />
              <ColWithGap>
                <VerticalFieldAndValueWithIcon
                  label={tr(`EditReservationSlotSidebar.forReservation`, `For reservation`)}
                  valueText={applicationId}
                  iconPath={'/images/icons/ringil3/forExpedition.svg'}
                />
                <HorizontalLine variant={'tertiary'} />
                <COHeadline variant={'h3'} title={tr(`EditReservationOrdersSidebar.cargo`, `Cargo`)}></COHeadline>
                {reservationActionTypeEnabled && (<VerticalFieldAndValue
                    label={tr(`EditReservationOrdersSidebar.manipulationType`, `Manipulation type`)}
                    labelMode={'normal'}>
                    <>
                      <InputManipulationType
                        mandatory={reservationActionTypeRequired}
                        loadingTypes={form.values.actionTypes}
                        onChange={v => {
                          form.setFieldValue(form.names.actionTypes, v);
                        }}
                      />
                      {findErrorMessage(form.validationErrors, 'actionTypes', tr) && (
                        <div className={'text-complementary-error text-[10px]'}>
                          {findErrorMessage(form.validationErrors, 'actionTypes', tr)}
                        </div>
                      )}
                    </>
                  </VerticalFieldAndValue>
                )}

                <Tabs
                  tabs={form?.values.orders.map((_, oIndex) => ({
                    label: `${oIndex + 1}.`,
                    active: activeTab === oIndex,
                    anchorId: `order_${oIndex}`,
                  }))}
                  onTabChange={setActiveTab}
                  tailSpacer={false}
                />
                <ColOdd>
                  <OrdersComponent
                    orderTitle={tr('OrdersComponent.orderReference', 'Order/Reference')}
                    orders={form.values.orders}
                    companyId={companyLocationCustomerId}
                    config={generateTimeslotOrderConfig(requiredOderItems, optionalDimensions ? 'optional' : 'never')}
                    onChange={v =>
                      form.useProduce(d => {
                        d.orders = v;
                      })
                    }
                    validationErrors={form.validationErrors}
                    externalOrders={externalOrdersResponse?.orders}
                    externalIdPresent={isExternalIdPresent(externalOrdersResponse)}
                  />
                </ColOdd>
                {showPlan && (
                  <>
                    <HorizontalLine variant={'tertiary'} />
                    <COHeadline variant={'h3'} title={tr(`CreateTimeslotSidebar.plan`, `Plan`)}></COHeadline>
                    <VerticalFieldAndValue
                      label={tr(`CreateTimeslotSidebar.localDate`, `Date`)}
                      labelMode={'normal'}
                      errorMessage={findValidationMessage(form.validationErrors, 'timeslot.localDate', tr)}
                    >
                      <RingilCalendar
                        value={form.values.localDate}
                        onChange={v => form.setFieldValue('localDate', v)}
                        fullWidth
                        minDate={externalOrdersMinDate}
                        maxDate={externalOrdersMaxDate}
                      />
                    </VerticalFieldAndValue>
                    {!slots && (
                      <LinkWithIcon
                        icon={<SvgAvailableDates />}
                        label={tr(`CreateTimeslotSidebar.offerAvailableSlots`, `Offer available slots`)}
                        onClick={() => {
                          offerAvailableSlots();
                        }}
                      />
                    )}

                    {slots && (
                      <Panel onClose={() => setSlots(undefined)}
                             headingText={tr('CreateTimeslotSidebar.availableSlots', 'Available slots')}
                             closeIcon={<SvgClose />}>
                        <ColWithGap>
                          {slots[0]?.freeSlots?.map((s, i) => {
                            return (
                              <VerticalFieldAndValue label={s.name} labelMode={'normal'} key={i}>
                                {s.slots.length == 0 && (
                                  <NoAvailableSlotsMessage unavailability={s.unavailability} />
                                )}
                                <div className="grid grid-cols-4">
                                  {s.slots.map((slot, j) => {
                                    const slotTz = slot.timezoneId ?? 'Europe/Prague';
                                    const slotSince = slot.utcStart ?? slot.since;
                                    const slotTill = slot.utcEnd ?? slot.till;
                                    return (
                                      <ClickableCursor onClick={() => {
                                        form.setFieldValue(form.names.arrivalHourString, stringHHmmFromStringInTz(slotSince, slotTz));
                                        form.setFieldValue(form.names.rampId, s.rampId);
                                        setSlots(undefined);
                                      }}>
                                        <COSelect
                                          text={`${stringHHmmFromStringInTz(slotSince, slotTz)} - ${stringHHmmFromStringInTz(slotTill, slotTz)}`}
                                          key={j} />
                                      </ClickableCursor>
                                    );
                                  })}
                                </div>
                              </VerticalFieldAndValue>
                            );
                          })}
                        </ColWithGap>
                      </Panel>
                    )}
                    <TwoColGrid>
                      <VerticalFieldAndValue
                        label={tr(`CreateTimeslotSidebar.ramp`, `Ramp`)}
                        labelMode={'normal'}
                        errorMessage={findValidationMessage(form.validationErrors, 'rampId', tr)}
                      >
                        <InputCompanyRamp
                          value={form.values.rampId}
                          onChange={v => form.setFieldValue(form.names.rampId, v)}
                          companyLocationId={form.values.companyLocationId}
                          companyId={companyLocationCustomerId}
                          showTemporaryClosed={false}
                        />
                      </VerticalFieldAndValue>
                      <VerticalFieldAndValue
                        label={tr(`CreateTimeslotSidebar.duration`, `Duration`)}
                        labelMode={'normal'}
                        errorMessage={findValidationMessage(form.validationErrors, 'durationInMin', tr)}
                      >
                        <InputTextWithSuffix
                          suffix={'min'}
                          value={form.values.durationInMin}
                          onChange={v => form.setFieldValue(form.names.durationInMin, parseNumberOrUndefined(v))}
                          readOnly={!canEditDuration}
                        />
                      </VerticalFieldAndValue>
                    </TwoColGrid>
                    <VerticalFieldAndValue
                      label={tr(`CreateTimeslotSidebar.arrival`, `Arrival`)}
                      labelMode={'normal'}
                      errorMessage={findValidationMessage(form.validationErrors, 'arrivalHourString', tr)}
                    >
                      <InputHourManual value={form.values.arrivalHourString}
                                       onChange={v => form.setFieldValue(form.names.arrivalHourString, v)} />
                    </VerticalFieldAndValue>
                    <RowWithGap>
                      <LinkWithIcon
                        icon={<SvgRefresh />}
                        label={tr(`CreateTimeslotSidebar.checkReservation`, `Check reservation`)}
                        onClick={() => {
                          checkReservationHandler();
                        }}
                      />

                      {checkReservationRequest && (
                        <>
                          <ProgressSpinner
                            style={{
                              width: '15px',
                              height: '15px',
                              marginRight: '0.5rem',
                              color: 'var(--ringil3-brand-color)',
                            }}
                            strokeWidth="3"
                            animationDuration="1s"
                          />
                        </>
                      )}
                    </RowWithGap>
                    <ReservationCheckResult valid={reservationValid} messages={reservationCheckMessages} />
                  </>
                )}
              </ColWithGap>
            </>
          )}
        </RingilSidebar>
      </>
    );
  };

  return { EditReservationOrdersSidebar, setVisible, visible };
};
